import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { formatDateWithTime } from "src/utils/string/stringUtil";
import { defaultPRD3020_R01DetailDto, PRD3020_R01DetailDto } from "./Dto_PRD3020_R01";
import { usePRD3020_R01Store } from "./Store_PRD3020_R01";
import { formatDowntimeMinutes, getDowntimeMinutes, headerNames_PRD3020_R01_Detail } from "./Util_PRD3020_R01";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

interface Props {
	gridRef: React.RefObject<AgGridReact<PRD3020_R01DetailDto> | null>;
}

const COLUMN_STATE_KEY = 'PRD3020_R01-detail-grid-column-state';

export const DetailTable_PRD3020_R01 = ({ gridRef }: Props) => {
	const {
		PRD3020_R01DetailList,
		selectedPRD3020_R01,
		detailColumnState,
		setDetailColumnState,
		detailScrollTop,
		setDetailScrollTop,
	} = usePRD3020_R01Store();
	const { downtimeValueList } = useCommonCodeStore();
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const selectedDetailList = useMemo(() => {
		if (!selectedPRD3020_R01?.productResultNo) return [];
		return PRD3020_R01DetailList.filter(
			(detail) => detail.productResultNo === selectedPRD3020_R01.productResultNo,
		);
	}, [PRD3020_R01DetailList, selectedPRD3020_R01?.productResultNo]);

	useEffect(() => {
		setDetailScrollTop(null);
		gridRef.current?.api?.ensureIndexVisible(0, 'top');
	}, [gridRef, selectedPRD3020_R01?.productResultNo, setDetailScrollTop]);

	const selectedDowntimeMinutes = useMemo(() =>
		selectedDetailList.reduce(
			(sum, detail) => sum + (getDowntimeMinutes(detail.fromTime, detail.toTime) ?? 0),
			0,
		),
	[selectedDetailList]);

	const {
		getRowId,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent<PRD3020_R01DetailDto>({
		gridRef,
		key: 'downtimeNo',
		gridData: selectedDetailList,
		defaultGridData: defaultPRD3020_R01DetailDto,
		selectedRow: undefined,
	});

	const baseColumnDefs: ColDef<PRD3020_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: ICellRendererParams<PRD3020_R01DetailDto>) =>
				params.node.rowPinned === 'bottom' ? '계' : (params.node.rowIndex ?? 0) + 1,
		),
		{
			headerName: '비가동번호', colId: 'downtimeNo', field: 'downtimeNo', width: 190,
			editable: false,
		},
		{
			headerName: '비가동사유', colId: 'downtimeCase', field: 'downtimeCase', width: 250,
			...CommonCodeColumn.options(downtimeValueList, false, undefined, undefined),
			editable: false,
		},
		{
			headerName: '비가동시작시간', colId: 'fromTime', field: 'fromTime', width: 200,
			valueFormatter: (params) => {
				if (params.node?.rowPinned === 'bottom') return '';
				return params.data?.downtimeNo && params.data.fromTime
					? formatDateWithTime(params.data.fromTime)
					: '';
			},
			comparator: cellDateComparator,
			editable: false,
		},
		{
			headerName: '비가동종료시간', colId: 'toTime', field: 'toTime', width: 200,
			valueFormatter: (params) => {
				if (params.node?.rowPinned === 'bottom') return '';
				return params.data?.downtimeNo && params.data.toTime
					? formatDateWithTime(params.data.toTime)
					: '';
			},
			comparator: cellDateComparator,
			editable: false,
		},
		{
			headerName: '비가동시간', colId: 'downtimeMinutes', width: 120,
			cellStyle: { textAlign: 'right', justifyContent: 'flex-end' },
			valueGetter: (params) => {
				if (params.node?.rowPinned === 'bottom') return selectedDowntimeMinutes;
				if (!params.data?.downtimeNo) return null;
				return getDowntimeMinutes(params.data.fromTime, params.data.toTime);
			},
			valueFormatter: (params) => formatDowntimeMinutes(params.value),
			comparator: (valueA, valueB) => Number(valueA ?? 0) - Number(valueB ?? 0),
			editable: false,
		},
	], [cellDateComparator, downtimeValueList, selectedDowntimeMinutes]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
		[baseColumnDefs],
	);

	const columnDefs: ColDef<PRD3020_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<PRD3020_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames: headerNames_PRD3020_R01_Detail,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, detailColumnState]);

	const pinnedBottomRowData = useMemo<PRD3020_R01DetailDto[]>(() => [{
		...defaultPRD3020_R01DetailDto,
		downtimeNo: `${selectedDetailList.length.toLocaleString()}건`,
		fromTime: undefined,
		toTime: undefined,
	}], [selectedDetailList.length]);

	return (
		<GridContextWrapper<PRD3020_R01DetailDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			defaultRowData={defaultPRD3020_R01DetailDto}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<PRD3020_R01DetailDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<PRD3020_R01DetailDto>({
					idKey: 'downtimeNo',
					selectedList: [],
					deletedList: [],
				})}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRowData}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
		</GridContextWrapper>
	);
};
